import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { BrButton, BrInput } from '@govbr-ds/webcomponents-angular/standalone';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  ɵInternalFormsSharedModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CardCollapseComponent } from '../../../../shared/components/collapse/card-collapse.component';
import { AlertService } from '../../../../shared/services/alert.service';
import { endOfDay, parse, startOfDay } from 'date-fns';

@Component({
  selector: 'app-confirmar-quantidade-vagas-step',
  templateUrl: './confirmar-quantidade-vagas-step.component.html',
  styleUrls: ['./confirmar-quantidade-vagas-step.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BrButton,
    BrInput,
    ɵInternalFormsSharedModule,
    CardCollapseComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ConfirmarQuantidadeVagasStepComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() onConfirmarQuantidades!: EventEmitter<void>;
  @Input() onCancel!: EventEmitter<void>;
  @Input() onSubmitting!: EventEmitter<boolean>;
  @Input() onModalidadeVagas!: EventEmitter<any[]>;
  @Input() onPrintCadastroReserva!: EventEmitter<void>;
  @Input() hasCadastroReserva: boolean = false;
  @Input() confirmacaoVaga: any;
  @Input() cicloSelected: any;
  @Input() isVagasLoaded: boolean = false;

  private readonly alertService = inject(AlertService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  isSubmitting = false;
  modalidadeVagas: any[] = [];

  ngOnInit(): void {
    this.onModalidadeVagas.subscribe({
      next: (modalidadeVagas: any[]) => {
        this.buidForm(modalidadeVagas);

        this.cdr.detectChanges();
      },
    });
    this.onSubmitting.subscribe((isSubmitting: boolean) => {
      this.isSubmitting = isSubmitting;
      this.cdr.detectChanges();
    });
  }

  get modalidadeVaga(): FormArray {
    return this.form.get('modalidadeVaga') as FormArray;
  }

  isFormValid(): boolean {
    return this.form.valid && this.modalidadeVaga.length > 0;
  }

  isPeriodoVigenteForEnable(): boolean {
    if (this.confirmacaoVaga?.rascunho === false) {
      return false;
    }
    if (this.cicloSelected) {
      const now = new Date();
      const dtInicio = startOfDay(
        parse(
          this.cicloSelected.confirmacaoVaga.inicio,
          'yyyy-MM-dd',
          new Date(),
        ),
      );
      const dtFim = endOfDay(
        parse(this.cicloSelected.confirmacaoVaga.fim, 'yyyy-MM-dd', new Date()),
      );
      return now >= dtInicio && now <= dtFim;
    }
    return false;
  }

  next(): void {
    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      return;
    }
    if (!this.valideVagas()) {
      this.alertService.warning(
        'Preencha a quantidade de vagas para cada equipe. A quantidade pode ser zero (0) se não houver vagas para a equipe, mas pelo menos uma equipe deve ter vagas.',
      );
      return;
    }
    this.isSubmitting = true;
    this.onConfirmarQuantidades.emit(this.buildRequest());
  }

  cancel(): void {
    this.alertService.confirm(
      'Deseja realmente cancelar? As informações não salvas serão perdidas.',
      () => {
        this.onCancel.emit();
      },
    );
  }

  private valideVagas(): boolean {
    return this.modalidadeVaga.controls.some((control) => {
      console.log(control.get('vagasConfirmadas')?.value);
      return (
        control.get('vagasConfirmadas')?.value !== null &&
        control.get('vagasConfirmadas')?.value !== '' &&
        control.get('vagasConfirmadas')?.value > 0
      );
    });
  }

  private buidForm(modalidadeVagas: any[]): void {
    const data = this.confirmacaoVaga
      ? JSON.parse(this.confirmacaoVaga.conteudo)
      : [];

    if (this.confirmacaoVaga?.rascunho === false) {
      modalidadeVagas = data;
    }

    let i = 0;

    this.modalidadeVaga?.clear();
    modalidadeVagas.forEach((mv) => {
      mv.data.forEach((item: any) => {
        const vagaConfirmada =
          data
            .map((d: any) => {
              if (d.idModalidade == mv.idModalidade) {
                return (
                  d.data.find((v: any) => v.tipoEquipe == item.tipoEquipe)
                    ?.vagasConfirmadas || 0
                );
              }
              return null;
            })
            .find((v: any) => v !== null) ?? '';

        item.index = i++;

        this.modalidadeVaga.push(
          this.formBuilder.group({
            id: [mv.idModalidade],
            nome: [mv.modalidade],
            totalVagas: [item.totalVagas],
            tipoEquipe: [item.tipoEquipe],
            index: [item.index],
            vagasConfirmadas: [
              {
                value: vagaConfirmada,
                disabled: !this.isPeriodoVigenteForEnable(),
              },
              [
                Validators.required,
                Validators.min(0),
                Validators.max(item.totalVagas),
              ],
            ],
          }),
        );
      });
    });

    this.modalidadeVagas = modalidadeVagas;
    this.cdr.detectChanges();

    if (this.confirmacaoVaga) {
      this.form.markAllAsTouched();
    }
  }

  private buildRequest(): any {
    const data: any[] = [];
    this.modalidadeVaga.controls.forEach((control) => {
      const index = data.findIndex(
        (value) => value.idModalidade === control.get('id')?.value,
      );

      if (index === -1) {
        data.push({
          idModalidade: control.get('id')?.value,
          modalidade: control.get('nome')?.value,
          data: [
            {
              tipoEquipe: control.get('tipoEquipe')?.value,
              totalVagas: control.get('totalVagas')?.value,
              vagasConfirmadas: control.get('vagasConfirmadas')?.value,
            },
          ],
        });
      } else {
        data[index].data.push({
          tipoEquipe: control.get('tipoEquipe')?.value,
          totalVagas: control.get('totalVagas')?.value,
          vagasConfirmadas: control.get('vagasConfirmadas')?.value,
        });
      }
    });

    return {
      vagas: JSON.stringify(data),
    };
  }
}
